import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Select,
  DatePicker,
  Switch,
  Input,
  Space,
  message,
  Empty,
} from "antd";
import dayjs from "dayjs";
import {
  fetchSubscriptionsByCompany,
  addSubscription,
  cancelSubscription,
} from "../../redux/companySubscription.slice";
import { fetchLicenses } from "../../redux/license.slice";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const STATUS_MAP = {
  0: { color: "orange", labelKey: "subscriptions.status.trial" },
  1: { color: "success", labelKey: "subscriptions.status.active" },
  2: { color: "error", labelKey: "subscriptions.status.expired" },
  3: { color: "default", labelKey: "subscriptions.status.cancelled" },
};

const SubscriptionsTab = ({companyId}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { subscriptions, loading } = useSelector(
    (state) => state.companySubscription,
  );
  const { licenses } = useSelector((state) => state.license);

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [form] = Form.useForm();
  const [cancelForm] = Form.useForm();

  useEffect(() => {
    if (companyId) {
      dispatch(fetchSubscriptionsByCompany(companyId));
      dispatch(fetchLicenses());
    }
  }, [companyId, dispatch]);

  const handleAssign = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        companyId,
        planId: values.planId,
        startDate: values.startDate.format("YYYY-MM-DD"),
        endDate: values.endDate.format("YYYY-MM-DD"),
        status: values.status,
        autoRenew: values.autoRenew ?? false,
      };
      await dispatch(addSubscription(payload)).unwrap();
      message.success(t("subscriptions.assignSuccess"));
      form.resetFields();
      setShowAssignModal(false);
      dispatch(fetchSubscriptionsByCompany(companyId));
    } catch (err) {
      if (err?.errorFields) return;
      message.error(t("subscriptions.assignError"));
    }
  };

  const askCancel = (sub) => {
    setSubscriptionToCancel(sub.subscriptionId);
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    try {
      const values = await cancelForm.validateFields();
      await dispatch(
        cancelSubscription({ id: subscriptionToCancel, reason: values.reason }),
      ).unwrap();
      message.success(t("subscriptions.cancelSuccess"));
      cancelForm.resetFields();
      setShowCancelModal(false);
      dispatch(fetchSubscriptionsByCompany(companyId));
    } catch (err) {
      if (err?.errorFields) return;
      message.error(t("subscriptions.cancelError"));
    }
  };

  const columns = [
    {
      title: t("subscriptions.plan"),
      dataIndex: "planName",
      render: (v) => v || "-",
    },
    {
      title: t("subscriptions.startDate"),
      dataIndex: "startDate",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-"),
    },
    {
      title: t("subscriptions.endDate"),
      dataIndex: "endDate",
      render: (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "-"),
    },
    {
      title: t("subscriptions.status"),
      dataIndex: "status",
      render: (status) => {
        const info = STATUS_MAP[status] || STATUS_MAP[2];
        return <Tag color={info.color}>{t(info.labelKey)}</Tag>;
      },
    },
    {
      title: t("subscriptions.autoRenew"),
      dataIndex: "autoRenew",
      render: (v) => (
        <Tag color={v ? "blue" : "default"}>
          {v ? t("common.yes") : t("common.no")}
        </Tag>
      ),
    },
    {
      title: t("subscriptions.cancellationReason"),
      dataIndex: "cancellationReason",
      render: (v) => v || "-",
    },
    {
      title: t("common.action"),
      key: "actions",
      render: (_, record) => {
        const isActive = record.status === 0 || record.status === 1;
        return isActive ? (
          <Button size="small" danger onClick={() => askCancel(record)}>
            {t("subscriptions.cancel")}
          </Button>
        ) : (
          <span style={{ color: "#999" }}>-</span>
        );
      },
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <span>{t("subscriptions.title")}</span>
            <Button
              type="primary"
              size="small"
              onClick={() => setShowAssignModal(true)}
            >
              <i className="ti ti-plus me-1"></i>
              {t("subscriptions.assignPlan")}
            </Button>
          </Space>
        }
      >
        {loading ? (
          <Empty description={t("common.loadingData")} />
        ) : (
          <Table
            columns={columns}
            dataSource={subscriptions || []}
            rowKey="subscriptionId"
            pagination={false}
            locale={{
              emptyText: (
                <Empty description={t("subscriptions.noSubscriptions")} />
              ),
            }}
          />
        )}
      </Card>

      <Modal
        title={t("subscriptions.assignPlan")}
        open={showAssignModal}
        onCancel={() => {
          setShowAssignModal(false);
          form.resetFields();
        }}
        onOk={handleAssign}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="planId"
            label={t("subscriptions.plan")}
            rules={[
              { required: true, message: t("subscriptions.planRequired") },
            ]}
          >
            <Select placeholder={t("common.select")}>
              {(licenses || [])
                .filter((l) => l.licenseIsActive)
                .map((l) => (
                  <Option
                    key={l.licenseId || l.lisence_id}
                    value={l.licenseId || l.lisence_id}
                  >
                    {l.licenseName}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="status"
            label={t("subscriptions.status")}
            initialValue={0}
            rules={[{ required: true }]}
          >
            <Select>
              <Option value={0}>{t("subscriptions.status.trial")}</Option>
              <Option value={1}>{t("subscriptions.status.active")}</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="startDate"
            label={t("subscriptions.startDate")}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="endDate"
            label={t("subscriptions.endDate")}
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item
            name="autoRenew"
            label={t("subscriptions.autoRenew")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("subscriptions.cancelTitle")}
        open={showCancelModal}
        onCancel={() => {
          setShowCancelModal(false);
          cancelForm.resetFields();
        }}
        onOk={handleCancel}
        okButtonProps={{ danger: true }}
        destroyOnHidden
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item
            name="reason"
            label={t("subscriptions.cancellationReason")}
            rules={[
              { required: true, message: t("subscriptions.reasonRequired") },
            ]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
SubscriptionsTab.propTypes = {
  companyId: PropTypes.string.isRequired,
};

export default SubscriptionsTab;
